import { useState, type Ref } from 'react';
import {
  FORM_LIMITS,
  isRequired,
  textIn,
  type Lang,
  type Question,
  type SignatureAnswer,
} from '../logic/form-logic';
import type { FormCopy } from './copy';
import { SignatureField } from './SignatureField';
import { cx, FieldError, FOCUS_RING } from './ui';

/** What a screen holds while the patient types: a number may still be "3,". */
export type DraftValue = unknown;

const HEADING = 'text-2xl font-semibold leading-snug tracking-tight text-heading focus:outline-none';
const CONTROL = cx(
  'mt-3 w-full rounded-lg border border-input bg-card px-3.5 py-2.5 text-lg text-foreground shadow-sm',
  'placeholder:text-muted-foreground aria-[invalid=true]:border-danger',
  FOCUS_RING,
);
const OPTION = cx(
  'flex min-h-[3.5rem] cursor-pointer items-center gap-3 rounded-xl border border-input bg-card px-4 py-3 text-lg text-foreground transition-colors',
  'hover:border-primary/60 has-[:checked]:border-primary has-[:checked]:bg-accent has-[:checked]:font-medium',
  'has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-ring',
);
const MARK = 'h-6 w-6 shrink-0 accent-[hsl(var(--primary))] focus:outline-none';

function Required({ copy }: { copy: FormCopy }) {
  return (
    <span className="ml-2 align-middle text-base font-normal text-muted-foreground">
      ({copy.required})
    </span>
  );
}

function maxLengthOf(q: Question): number {
  const cap = q.type === 'short_text' ? FORM_LIMITS.shortText : FORM_LIMITS.longText;
  return (q.type === 'short_text' || q.type === 'long_text') &&
    typeof q.maxLength === 'number' &&
    q.maxLength > 0
    ? Math.min(q.maxLength, cap)
    : cap;
}

/** A number as typed, kept as text until it reads as one ("3,5" is 3.5). */
function NumberInput({
  id,
  value,
  onChange,
  integer,
  describedBy,
  invalid,
}: {
  id: string;
  value: DraftValue;
  onChange: (value: DraftValue) => void;
  integer: boolean;
  describedBy?: string;
  invalid: boolean;
}) {
  const [text, setText] = useState(
    typeof value === 'number' ? String(value) : typeof value === 'string' ? value : '',
  );
  return (
    <input
      id={id}
      type="text"
      inputMode={integer ? 'numeric' : 'decimal'}
      autoComplete="off"
      value={text}
      onChange={(event) => {
        const raw = event.target.value;
        setText(raw);
        const trimmed = raw.trim();
        if (trimmed === '') {
          onChange(undefined);
          return;
        }
        const parsed = /^-?\d+(?:[.,]\d+)?$/.test(trimmed)
          ? Number(trimmed.replace(',', '.'))
          : Number.NaN;
        onChange(Number.isFinite(parsed) ? parsed : trimmed);
      }}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      className={cx(CONTROL, 'max-w-[12rem]')}
    />
  );
}

/**
 * One question, on its own screen (SFM-D1). The question is the screen's
 * heading, so focus can land on it and a screen reader says where the patient
 * is. Help and errors are tied to the control by `aria-describedby`.
 */
export function QuestionView({
  idPrefix,
  question: q,
  lang,
  copy,
  value,
  onChange,
  error,
  errorLang,
  headingRef,
  today,
}: {
  idPrefix: string;
  question: Question;
  lang: Lang;
  copy: FormCopy;
  value: DraftValue;
  onChange: (value: DraftValue) => void;
  /** The error message to show, already in words. */
  error: string | null;
  /** The language `error` is written in, when it is not the form's (SFM-D33). */
  errorLang?: Lang;
  headingRef: Ref<HTMLHeadingElement>;
  /** `YYYY-MM-DD` in the pharmacy's time zone. */
  today: string;
}) {
  const id = `${idPrefix}-${q.id}`;
  const helpId = q.help ? `${id}-help` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = cx(helpId, errorId) || undefined;
  const label = textIn(q.label, lang);
  const required = isRequired(q);
  const help = q.help ? (
    <p id={helpId} className="mt-2 text-base text-muted-foreground">
      {textIn(q.help, lang)}
    </p>
  ) : null;
  const errorLine = error ? (
    <FieldError id={errorId!} lang={errorLang}>
      {error}
    </FieldError>
  ) : null;

  if (q.type === 'info') {
    return (
      <div>
        <h2 ref={headingRef} tabIndex={-1} className={HEADING}>
          {copy.infoHeading}
        </h2>
        <div className="mt-4 whitespace-pre-line text-lg leading-relaxed text-foreground">
          {label}
        </div>
        {help}
      </div>
    );
  }

  if (
    q.type === 'single_choice' ||
    q.type === 'yes_no' ||
    q.type === 'multi_choice' ||
    q.type === 'consent'
  ) {
    return (
      <fieldset id={id} aria-describedby={describedBy}>
        <legend className="w-full">
          <h2 ref={headingRef} tabIndex={-1} className={HEADING}>
            {label}
            {required && <Required copy={copy} />}
          </h2>
        </legend>
        {help}
        {q.type === 'multi_choice' && (
          <p className="mt-2 text-base text-muted-foreground">
            {typeof q.maxSelected === 'number' ? copy.chooseUpTo(q.maxSelected) : copy.chooseAll}
          </p>
        )}
        {errorLine}
        {q.type === 'consent' && (
          <div className="mt-4 whitespace-pre-line rounded-xl bg-muted p-4 text-lg leading-relaxed text-foreground">
            {textIn(q.statement, lang)}
          </div>
        )}
        <div className="mt-4 space-y-3">
          {q.type === 'consent' ? (
            <label className={OPTION}>
              <input
                type="checkbox"
                className={MARK}
                checked={value === true}
                onChange={(event) => onChange(event.target.checked ? true : undefined)}
                aria-invalid={Boolean(error) || undefined}
              />
              <span>{copy.agree}</span>
            </label>
          ) : q.type === 'multi_choice' ? (
            q.options.map((o) => {
              const picked = Array.isArray(value) ? (value as string[]) : [];
              return (
                <label key={o.id} className={OPTION}>
                  <input
                    type="checkbox"
                    className={MARK}
                    name={id}
                    value={o.id}
                    checked={picked.includes(o.id)}
                    onChange={(event) => {
                      const next = event.target.checked
                        ? [...picked, o.id]
                        : picked.filter((p) => p !== o.id);
                      // In the form's own order, whatever order they were ticked.
                      const ordered = q.options.map((x) => x.id).filter((x) => next.includes(x));
                      onChange(ordered.length > 0 ? ordered : undefined);
                    }}
                    aria-invalid={Boolean(error) || undefined}
                  />
                  <span>{textIn(o.label, lang)}</span>
                </label>
              );
            })
          ) : (
            (q.type === 'yes_no'
              ? [
                  { id: 'yes', label: copy.yes },
                  { id: 'no', label: copy.no },
                ]
              : q.options.map((o) => ({ id: o.id, label: textIn(o.label, lang) }))
            ).map((o) => (
              <label key={o.id} className={OPTION}>
                <input
                  type="radio"
                  className={MARK}
                  name={id}
                  value={o.id}
                  checked={value === o.id}
                  onChange={() => onChange(o.id)}
                  aria-invalid={Boolean(error) || undefined}
                />
                <span>{o.label}</span>
              </label>
            ))
          )}
        </div>
      </fieldset>
    );
  }

  if (q.type === 'signature') {
    return (
      <div id={id} role="group" aria-labelledby={`${id}-heading`}>
        <h2 id={`${id}-heading`} ref={headingRef} tabIndex={-1} className={HEADING}>
          {label}
          {required && <Required copy={copy} />}
        </h2>
        {help}
        {errorLine}
        <div className="mt-4">
          <SignatureField
            id={id}
            value={value as SignatureAnswer | undefined}
            onChange={onChange}
            copy={copy}
            describedBy={describedBy}
            invalid={Boolean(error)}
          />
        </div>
      </div>
    );
  }

  // Text, a date or a number: one control, labelled by the heading itself.
  const heading = (
    <h2 ref={headingRef} tabIndex={-1} className={HEADING}>
      <label htmlFor={id}>
        {label}
        {required && <Required copy={copy} />}
      </label>
    </h2>
  );
  const common = {
    id,
    'aria-invalid': Boolean(error) || undefined,
    'aria-describedby': describedBy,
  } as const;

  if (q.type === 'number') {
    return (
      <div>
        {heading}
        {help}
        {errorLine}
        <div className="flex items-center gap-3">
          <NumberInput
            id={id}
            value={value}
            onChange={onChange}
            integer={Boolean(q.integer)}
            describedBy={describedBy}
            invalid={Boolean(error)}
          />
          {q.unit && <span className="mt-3 text-lg text-foreground">{textIn(q.unit, lang)}</span>}
        </div>
      </div>
    );
  }

  if (q.type === 'date') {
    return (
      <div>
        {heading}
        {help}
        {errorLine}
        <input
          {...common}
          type="date"
          value={typeof value === 'string' ? value : ''}
          min={q.range === 'future' ? today : undefined}
          max={q.range === 'past' ? today : undefined}
          onChange={(event) => onChange(event.target.value || undefined)}
          className={cx(CONTROL, 'max-w-[16rem]')}
        />
      </div>
    );
  }

  const max = maxLengthOf(q);
  const text = typeof value === 'string' ? value : '';
  const counterId = `${id}-count`;
  return (
    <div>
      {heading}
      {help}
      {errorLine}
      {q.type === 'long_text' ? (
        <>
          <textarea
            {...common}
            aria-describedby={cx(describedBy, counterId)}
            rows={6}
            maxLength={max}
            value={text}
            onChange={(event) => onChange(event.target.value || undefined)}
            className={CONTROL}
          />
          <p id={counterId} className="mt-1 text-base text-muted-foreground">
            {copy.charactersLeft(Math.max(max - text.length, 0))}
          </p>
        </>
      ) : (
        <input
          {...common}
          type="text"
          autoComplete="off"
          maxLength={max}
          value={text}
          onChange={(event) => onChange(event.target.value || undefined)}
          className={CONTROL}
        />
      )}
    </div>
  );
}
